import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import html2canvas from "html2canvas";
import {
  Map as MapIcon, Home as HomeIcon, Car, Layers, Loader2, Sparkles,
  BarChart3, DollarSign, MapPin, Gauge, Activity, TrendingUp, TrendingDown, Minus, ArrowLeft,
  GitCompareArrows, Download, X, ArrowUp, Info, Zap, Search, ChevronsUpDown,
  Bookmark, BookmarkPlus, Trash2, Flame,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAnalyst } from "@/context/AnalystContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import usStates from "@/lib/us-states.geojson.json";
import { NAME_TO_CODE } from "@/lib/stateNames";

const CODE_TO_NAME = Object.fromEntries(Object.entries(NAME_TO_CODE).map(([n, c]) => [c, n]));
const US_CENTER = [39.5, -98.35];
const US_BOUNDS = L.latLngBounds([[24, -125], [49.5, -66.5]]);
const RAMP_VOLUME = ["#0f172a", "#1e293b", "#1d4ed8", "#2563eb", "#3b82f6", "#60a5fa"];
const RAMP_VALUE = ["#171226", "#31103f", "#5b21b6", "#7c3aed", "#8b5cf6", "#a78bfa"];
const NO_DATA = "#12131c";

function colorFor(v, max, ramp) {
  if (!v || !max) return NO_DATA;
  const r = Math.sqrt(v / max);
  if (r < 0.1) return ramp[1];
  if (r < 0.28) return ramp[2];
  if (r < 0.5) return ramp[3];
  if (r < 0.75) return ramp[4];
  return ramp[5];
}
// Diverging red→neutral→green for the market-pulse (since last warm) view.
function pulseColor(d, maxAbs) {
  if (d == null || d === 0 || !maxAbs) return "#334155";
  const t = Math.min(1, Math.abs(d) / maxAbs);
  if (d > 0) return `rgba(16,185,129,${0.35 + 0.6 * t})`;   // emerald
  return `rgba(239,68,68,${0.35 + 0.6 * t})`;                // red
}
function fmtPrice(p) {
  if (!p) return "—";
  if (p >= 1_000_000) return `$${(p / 1_000_000).toFixed(p >= 10_000_000 ? 0 : 1)}M`;
  return `$${Math.round(p / 1000)}K`;
}

// Compact interactive SVG sparkline for the per-state Pulse Timeline (hover shows each day's figure).
function Sparkline({ points, dates = [], color = "#60a5fa", format = (v) => v, w = 168, h = 42 }) {
  const [hi, setHi] = useState(null);
  if (!points || points.length < 2) return null;
  const vals = points.map((v) => (v == null ? 0 : v));
  const min = Math.min(...vals), max = Math.max(...vals);
  const rng = max - min || 1;
  const pad = 4;
  const step = (w - pad * 2) / (vals.length - 1);
  const xy = vals.map((v, i) => [pad + i * step, h - pad - ((v - min) / rng) * (h - pad * 2)]);
  const line = xy.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} ${xy[xy.length - 1][0].toFixed(1)},${h - pad} ${xy[0][0].toFixed(1)},${h - pad}`;
  const last = xy[xy.length - 1];
  const active = hi != null ? xy[hi] : null;
  const gid = `sg-${color.replace(/[^a-z0-9]/gi, "")}`;
  return (
    <div className="relative" style={{ width: w, height: h }}>
      <svg width={w} height={h} className="overflow-visible" data-testid="pulse-sparkline">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polygon points={area} fill={`url(#${gid})`} />
        <polyline points={line} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
        {active && <line x1={active[0]} y1={0} x2={active[0]} y2={h} stroke={color} strokeOpacity="0.35" strokeWidth="1" />}
        <circle cx={last[0]} cy={last[1]} r="2.6" fill={color} />
        {active && <circle cx={active[0]} cy={active[1]} r="3.4" fill={color} stroke="#0b0c12" strokeWidth="1.6" />}
        {vals.map((v, i) => (
          <rect key={i} x={Math.max(0, xy[i][0] - step / 2)} y={0} width={step} height={h}
            fill="transparent" style={{ cursor: "crosshair" }}
            onMouseEnter={() => setHi(i)} onMouseLeave={() => setHi(null)} />
        ))}
      </svg>
      {active && (
        <div className="absolute z-10 pointer-events-none -translate-x-1/2 -translate-y-full" style={{ left: active[0], top: Math.max(active[1] - 6, 2) }} data-testid="pulse-sparkline-tooltip">
          <div className="rounded-md border border-border bg-background/95 backdrop-blur px-2 py-1 shadow-lg whitespace-nowrap">
            <div className="font-display text-xs leading-none">{format(points[hi])}</div>
            <div className="font-mono-data text-[9px] text-muted-foreground mt-0.5">{dates[hi] || ""}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CoverageMap() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const analyst = useAnalyst();
  const [tab, setTab] = useState("homes");
  const [metric, setMetric] = useState("volume");
  const [pulseMetric, setPulseMetric] = useState("count"); // count | median | sub
  const [pulseWindow, setPulseWindow] = useState("warm");   // warm | 7 | 30 (pulse comparison window)
  const [coverage, setCoverage] = useState({ states: {}, max_count: 0, max_median: 0, total: 0, state_count: 0, pulse_available: false });
  const [panel, setPanel] = useState(null);       // selected state (explore)
  const [areaView, setAreaView] = useState(null); // { area, city, count } when drilled into a ZIP/city
  const [loadingPts, setLoadingPts] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [cmp, setCmp] = useState([null, null]);    // two pinned states
  const [exporting, setExporting] = useState(false);
  const [areaOptions, setAreaOptions] = useState([]); // ZIP/city list for the jump dropdown
  const [stateOpen, setStateOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const [savedViews, setSavedViews] = useState([]);
  const [cmpAreaOpen, setCmpAreaOpen] = useState(null); // index of slot whose area dropdown is open
  const [cmpAreas, setCmpAreas] = useState({}); // { STATECODE: [areas] }
  const [areaMovers, setAreaMovers] = useState({ available: false, since: null, areas: [] });
  const [modelMovers, setModelMovers] = useState({ available: false, models: [] });
  const [moversMode, setMoversMode] = useState("cities"); // cities | models (vehicles only)
  const [digest, setDigest] = useState({ available: false });

  const mapEl = useRef(null);
  const captureEl = useRef(null);
  const mapRef = useRef(null);
  const geoLayer = useRef(null);
  const areaLayer = useRef(null);
  const clusterRef = useRef(null);
  const untrackedRef = useRef(null);
  const drillRef = useRef(null);
  const pinSlotRef = useRef(null);
  const lastZipsRef = useRef(null);
  const pendingRestoreRef = useRef(null);
  const tabRef = useRef(tab); tabRef.current = tab;
  const metricRef = useRef(metric); metricRef.current = metric;
  const pulseMetricRef = useRef(pulseMetric); pulseMetricRef.current = pulseMetric;
  const compareRef = useRef(compareMode); compareRef.current = compareMode;
  const panelRef = useRef(panel); panelRef.current = panel;

  const kindLabel = tab === "vehicles" ? "Vehicles" : "Homes";
  const areaWord = tab === "vehicles" ? "city" : "ZIP";
  const subLabel = tab === "vehicles" ? "Median Miles" : "$/Sqft";
  const fmtDelta = (pm, n) => {
    if (n == null) return "no change";
    const sign = n > 0 ? "+" : n < 0 ? "−" : "";
    if (pm === "median") return `${n > 0 ? "+" : n < 0 ? "−" : ""}${fmtPrice(Math.abs(n))}`;
    if (pm === "sub") return `${sign}${Math.abs(n).toLocaleString()}${tab === "vehicles" ? " mi" : "/sf"}`;
    return `${sign}${Math.abs(n).toLocaleString()}`;
  };
  const ramp = metric === "value" ? RAMP_VALUE : RAMP_VOLUME;
  const accentRGB = metric === "value" ? "168,139,250" : "96,165,250";
  // Pulse shows red/green only when there's genuine day-over-day movement. Until the next daily warm
  // brings a change, there's nothing to tint — we fall back to inventory density + say so.
  const pulseLive = useMemo(() => (
    metric === "pulse" && coverage.pulse_available
    && Object.values(coverage.states).some((s) => s.deltas && (s.deltas[pulseMetric] || 0) !== 0)
  ), [metric, coverage, pulseMetric]);

  // ---- init map once ----
  useEffect(() => {
    if (!user || mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, {
      center: US_CENTER, zoom: 4, minZoom: 3, maxZoom: 14,
      zoomControl: true, attributionControl: false, scrollWheelZoom: true,
      maxBounds: US_BOUNDS.pad(0.6),
    });
    clusterRef.current = L.markerClusterGroup({
      showCoverageOnHover: false, maxClusterRadius: 45, chunkedLoading: true,
      iconCreateFunction: (c) => {
        const n = c.getChildCount(); const big = n >= 50;
        return L.divIcon({ html: `<div class="lf-cluster ${big ? "lf-cluster-lg" : ""}">${n}</div>`, className: "lf-cluster-wrap", iconSize: L.point(big ? 46 : 36, big ? 46 : 36) });
      },
    }).addTo(map);
    areaLayer.current = L.layerGroup().addTo(map);
    untrackedRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // Keep the map crisp when the container size settles / the window resizes (fixes
    // partially-rendered choropleth + mis-placed markers on first paint and layout shifts).
    setTimeout(() => map.invalidateSize(), 250);
    const onResize = () => map.invalidateSize();
    window.addEventListener("resize", onResize);

    map.on("popupopen", (e) => {
      const el = e.popup.getElement();
      const vb = el?.querySelector(".lf-view-btn");
      if (vb) vb.onclick = () => { window.location.href = `/${vb.dataset.kind}/${vb.dataset.id}`; };
      const ab = el?.querySelector(".lf-area-btn");
      if (ab) ab.onclick = () => { map.closePopup(); drillRef.current && drillRef.current(ab.dataset.area, ab.dataset.city); };
      el?.querySelectorAll(".lf-slot-btn").forEach((b) => {
        b.onclick = () => { map.closePopup(); pinSlotRef.current && pinSlotRef.current(Number(b.dataset.slot), b.dataset.code, b.dataset.name); };
      });
    });
    return () => { window.removeEventListener("resize", onResize); map.remove(); mapRef.current = null; };
  }, [user]);

  // ---- load coverage on tab change ----
  useEffect(() => {
    if (!user) return;
    setPanel(null); setAreaView(null); setCmp([null, null]); setAreaOptions([]);
    setAreaMovers({ available: false, since: null, areas: [] });
    setModelMovers({ available: false, models: [] }); setMoversMode("cities");
    api.get("/geo/digest", { params: { kind: tab } }).then((r) => setDigest(r.data)).catch(() => setDigest({ available: false }));
    lastZipsRef.current = null;
    clusterRef.current && clusterRef.current.clearLayers();
    areaLayer.current && areaLayer.current.clearLayers();
    api.get("/geo/coverage", { params: { kind: tab, window: pulseWindow } })
      .then((r) => setCoverage(r.data))
      .catch(() => setCoverage({ states: {}, max_count: 0, max_median: 0, total: 0, state_count: 0, pulse_available: false }));
  }, [tab, user, pulseWindow]);

  // ---- draw choropleth on coverage/metric change ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (geoLayer.current) { geoLayer.current.remove(); geoLayer.current = null; }
    untrackedRef.current && untrackedRef.current.clearLayers();

    const valOf = (code) => {
      const s = coverage.states[code]; if (!s) return 0;
      return metricRef.current === "value" ? s.median : s.count;
    };
    const maxV = metricRef.current === "value" ? coverage.max_median : coverage.max_count;
    const pm = pulseMetricRef.current;
    const dOf = (s) => (s && s.deltas ? (s.deltas[pm] || 0) : 0);
    const maxAbsDelta = Math.max(1, ...Object.values(coverage.states).map((s) => Math.abs(dOf(s))));
    // Pulse is "live" only when there's genuine day-over-day movement. Until then (identical warms /
    // baseline just set) we tint by inventory density instead of leaving every state dead-grey.
    const pulseLiveLocal = coverage.pulse_available
      && Object.values(coverage.states).some((s) => dOf(s) !== 0);

    const fillOf = (code) => {
      const s = coverage.states[code];
      if (!s) return NO_DATA;
      if (metricRef.current === "pulse") {
        return pulseLiveLocal ? pulseColor(dOf(s), maxAbsDelta) : colorFor(s.count, coverage.max_count, RAMP_VOLUME);
      }
      return colorFor(valOf(code), maxV, ramp);
    };

    const layer = L.geoJSON(usStates, {
      style: (f) => {
        const code = NAME_TO_CODE[f.properties.name];
        const has = !!coverage.states[code];
        return { fillColor: fillOf(code), weight: 1.2, color: has ? "#2b3448" : "#272a3b", fillOpacity: has ? 0.85 : 0.4 };
      },
      onEachFeature: (f, lyr) => {
        const name = f.properties.name; const code = NAME_TO_CODE[name];
        const s = coverage.states[code]; const count = s?.count || 0;
        const dl = s?.deltas?.[pm];
        const pulseTxt = metricRef.current === "pulse"
          ? `<br/><span style="color:${dl > 0 ? "#34d399" : dl < 0 ? "#f87171" : "#94a3b8"}">${dl ? (dl > 0 ? "▲ " : "▼ ") + fmtDelta(pm, dl) : "no change"} since last warm</span>`
          : "";
        lyr.bindTooltip(
          count ? `<b>${name}</b><br/>${count.toLocaleString()} ${tabRef.current === "vehicles" ? "vehicles" : "homes"} · med ${fmtPrice(s.median)}${pulseTxt}`
                : `<b>${name}</b><br/><span style="opacity:.6">Untracked</span>`,
          { sticky: true, className: "lf-tip" });
        lyr.on({
          mouseover: (e) => e.target.setStyle({ weight: 2.5, color: "#60a5fa", fillOpacity: 0.98 }),
          mouseout: (e) => geoLayer.current && geoLayer.current.resetStyle(e.target),
          click: (e) => {
            if (compareRef.current) {
              L.popup({ className: "lf-popup", closeButton: true, offset: [0, -2] })
                .setLatLng(e.latlng)
                .setContent(
                  `<div class="lf-pop"><div class="lf-title" style="margin-bottom:7px;font-weight:600">${name}</div>
                     <div class="lf-slot-row">
                       <button class="lf-slot-btn" data-slot="0" data-code="${code}" data-name="${name}">Pin to A</button>
                       <button class="lf-slot-btn" data-slot="1" data-code="${code}" data-name="${name}">Pin to B</button>
                     </div></div>`)
                .openOn(map);
              return;
            }
            // Clicking the background of the state you're already viewing/drilled into must NOT
            // zoom out or reload — only a deliberate zoom control or picking a different state changes view.
            if (panelRef.current?.code === code) return;
            map.flyToBounds(e.target.getBounds().pad(0.08), { duration: 0.6 });
            if (!count) { setPanel({ code, name, tracked: false }); setAreaView(null); clearMarkers(); return; }
            loadState(code, name);
          },
        });
        if (!count) {
          L.marker(lyr.getBounds().getCenter(), { interactive: false,
            icon: L.divIcon({ html: `<div class="lf-untracked">Untracked State</div>`, className: "lf-untracked-wrap", iconSize: [104, 18] }) }).addTo(untrackedRef.current);
        }
      },
    }).addTo(map);
    geoLayer.current = layer;
  }, [coverage, metric, pulseMetric]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearMarkers = () => { clusterRef.current && clusterRef.current.clearLayers(); areaLayer.current && areaLayer.current.clearLayers(); };

  // ---- state selected -> summary + ZIP/city heat bubbles ----
  const loadState = (code, name) => {
    const kind = tabRef.current;
    setAreaView(null); setLoadingPts(true); clearMarkers();
    if (kind === "vehicles") {
      api.get("/geo/model-movers", { params: { state: code } }).then((r) => setModelMovers(r.data)).catch(() => setModelMovers({ available: false, models: [] }));
    } else {
      setModelMovers({ available: false, models: [] });
    }
    Promise.all([
      api.get("/geo/points", { params: { kind, state: code, limit: 1 } }),
      api.get("/geo/zips", { params: { kind, state: code, limit: 400 } }),
      api.get("/geo/timeline", { params: { kind, state: code, days: 30 } }).catch(() => ({ data: { series: [] } })),
    ]).then(([pRes, zRes, tRes]) => {
      setPanel({ code, name, tracked: true, summary: pRes.data.summary, total: pRes.data.total, timeline: tRes.data.series || [] });
      renderAreas(zRes.data, kind);
    }).catch(() => setPanel({ code, name, tracked: true, summary: null }))
      .finally(() => setLoadingPts(false));
  };

  const renderAreas = (data, kind) => {
    lastZipsRef.current = data;
    setAreaOptions((data.areas || []).map((a) => ({ area: a.area, city: a.city, count: a.count, median: a.median, lat: a.lat, lng: a.lng })));
    setAreaMovers({ available: !!data.pulse_available, since: data.pulse_since || null,
                    areas: (data.areas || []).filter((a) => a.deltas).map((a) => ({ area: a.area, city: a.city, count: a.count, median: a.median, deltas: a.deltas, streak: a.streak })) });
    areaLayer.current.clearLayers();
    const isValue = metricRef.current === "value";
    const isPulse = metricRef.current === "pulse";
    const pm = pulseMetricRef.current === "median" ? "median" : "count"; // area pulse supports count & median
    const rampL = isValue ? RAMP_VALUE : RAMP_VOLUME;
    const maxColor = isValue ? (data.max_median || 1) : (data.max_count || 1);
    const maxSize = isValue ? (data.max_median || 1) : (data.max_count || 1);
    const glow = isValue ? "168,139,250" : "59,130,246";
    const areaDeltas = (data.areas || []).map((a) => (a.deltas ? (a.deltas[pm] || 0) : 0));
    const maxAbsDelta = Math.max(1, ...areaDeltas.map((d) => Math.abs(d)));
    // Render smallest first so denser/pricier bubbles sit on top and stay readable.
    [...(data.areas || [])].reverse().forEach((a) => {
      if (a.lat == null || a.lng == null) return;
      const sizeM = isValue ? a.median : a.count;   // size by density (value mode by median)
      const colorM = isValue ? a.median : a.count;
      const size = Math.round(28 + 38 * Math.sqrt(Math.min(1, (sizeM || 0) / maxSize)));
      const label = kind === "vehicles" ? a.city : a.area;
      const short = String(label || "").length > 12 ? String(label).slice(0, 11) + "…" : label;
      const delta = a.deltas ? (a.deltas[pm] || 0) : null;
      let bigNum, numFont, fill, glowRGB;
      if (isPulse) {
        numFont = 12;
        bigNum = delta == null ? "•" : delta === 0 ? "•" : `${delta > 0 ? "▲" : "▼"}${pm === "median" ? fmtPrice(Math.abs(delta)) : Math.abs(delta)}`;
        fill = pulseColor(delta, maxAbsDelta);
        glowRGB = delta > 0 ? "16,185,129" : delta < 0 ? "239,68,68" : "100,116,139";
      } else {
        bigNum = isValue ? fmtPrice(a.median) : a.count;
        numFont = isValue ? 11 : 13;
        fill = colorFor(colorM, maxColor, rampL);
        glowRGB = glow;
      }
      const m = L.marker([a.lat, a.lng], {
        icon: L.divIcon({
          className: "lf-area-wrap",
          html: `<div class="lf-area" style="width:${size}px;height:${size}px;background:${fill};box-shadow:0 0 ${Math.round(size / 3)}px rgba(${glowRGB},0.55)">
                   <div class="lf-area-n" style="font-size:${numFont}px">${bigNum}</div><div class="lf-area-z">${short}</div></div>`,
          iconSize: [size, size], iconAnchor: [size / 2, size / 2],
        }),
      });
      const zipTag = kind === "vehicles" ? "" : "ZIP ";
      const cityTag = kind === "vehicles" ? "" : (a.city ? " · " + a.city : "");
      const pulseTip = isPulse
        ? (a.deltas
            ? `<br/><span style="color:${delta > 0 ? "#34d399" : delta < 0 ? "#f87171" : "#94a3b8"}">${delta ? (delta > 0 ? "▲ " : "▼ ") + fmtDelta(pm, delta) : "no change"} since last warm</span>`
            : `<br/><span style="opacity:.6">pulse baseline set</span>`)
        : "";
      m.bindTooltip(`<b>${zipTag}${label}</b>${cityTag}<br/>${a.count} ${kind === "vehicles" ? "vehicles" : "homes"} · median ${fmtPrice(a.median)}${pulseTip}`, { className: "lf-tip", direction: "top" });
      m.bindPopup(
        `<div class="lf-pop">
           <div class="lf-price">${fmtPrice(a.median)} <span style="font-size:11px;opacity:.6">median</span></div>
           <div class="lf-title">${zipTag}${label}${cityTag}</div>
           <div class="lf-sub">${a.count} ${kind === "vehicles" ? "vehicles" : "homes"} tracked here</div>
           ${isPulse && a.deltas ? `<div class="lf-delta" style="color:${delta > 0 ? "#34d399" : delta < 0 ? "#f87171" : "#94a3b8"}">${delta ? (delta > 0 ? "▲ " : "▼ ") + fmtDelta(pm, delta) : "no change"} since last warm</div>` : ""}
           <button class="lf-area-btn" data-area="${a.area}" data-city="${a.city || ""}">Show listings here</button>
         </div>`, { className: "lf-popup", maxWidth: 230, minWidth: 190 });
      m.addTo(areaLayer.current);
    });
  };

  // Re-render ZIP/city bubbles when metric or pulse sub-metric changes (Value resizes by median; Pulse recolors by delta).
  useEffect(() => {
    if (panel?.tracked && !areaView && lastZipsRef.current) renderAreas(lastZipsRef.current, tabRef.current);
  }, [metric, pulseMetric]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- drill into one ZIP/city -> individual listings ----
  const drillArea = (area, city) => {
    const kind = tabRef.current; const code = panel?.code;
    if (!code) return;
    setLoadingPts(true); areaLayer.current.clearLayers(); clusterRef.current.clearLayers();
    api.get("/geo/points", { params: { kind, state: code, area, limit: 500 } })
      .then((r) => {
        setAreaView({ area, city: city || (r.data.points?.[0]?.city), count: r.data.total });
        const markers = []; const lls = [];
        (r.data.points || []).forEach((pt) => {
          if (pt.lat == null || pt.lng == null) return;
          lls.push([pt.lat, pt.lng]);
          const m = L.marker([pt.lat, pt.lng], { icon: L.divIcon({ html: `<span class="lf-dot ${kind === "vehicles" ? "lf-dot-veh" : ""}"></span>`, className: "lf-dot-wrap", iconSize: [14, 14] }) });
          const img = pt.image ? `<img src="${pt.image}" alt="" style="width:100%;height:96px;object-fit:cover;border-radius:8px;margin-bottom:8px"/>` : "";
          m.bindPopup(`<div class="lf-pop">${img}<div class="lf-price">${fmtPrice(pt.price)}</div><div class="lf-title">${pt.title || ""}</div><div class="lf-sub">${[pt.subtitle, pt.city].filter(Boolean).join(" · ")}</div><button class="lf-view-btn" data-id="${pt.id}" data-kind="${kind === "vehicles" ? "vehicles" : "homes"}">View in ${kind === "vehicles" ? "Vehicles" : "Homes"}</button></div>`, { className: "lf-popup", maxWidth: 240, minWidth: 200 });
          markers.push(m);
        });
        clusterRef.current.addLayers(markers);
        if (lls.length) mapRef.current.flyToBounds(L.latLngBounds(lls).pad(0.25), { duration: 0.6, maxZoom: 13 });
      })
      .finally(() => setLoadingPts(false));
  };
  drillRef.current = drillArea;

  const backToAreas = () => {
    const kind = tabRef.current; const code = panel?.code;
    setAreaView(null); clusterRef.current.clearLayers();
    api.get("/geo/zips", { params: { kind, state: code, limit: 400 } }).then((r) => renderAreas(r.data, kind));
    const f = geoLayer.current?.getLayers().find((l) => NAME_TO_CODE[l.feature.properties.name] === code);
    if (f) mapRef.current.flyToBounds(f.getBounds().pad(0.08), { duration: 0.5 });
  };

  // ---- jump-to (dropdown) + saved views ----
  const selectState = (code) => {
    const name = CODE_TO_NAME[code] || code;
    if (compareMode) { pinCompare(code, name); return; }
    const f = geoLayer.current?.getLayers().find((l) => NAME_TO_CODE[l.feature.properties.name] === code);
    if (f) mapRef.current.flyToBounds(f.getBounds().pad(0.08), { duration: 0.6 });
    loadState(code, name);
  };

  const saveCurrentView = () => {
    if (!panel?.code) return;
    const v = { kind: tab, code: panel.code, name: panel.name, area: areaView?.area || null, areaCity: areaView?.city || null };
    api.post("/geo/saved-views", v).then((r) => setSavedViews(r.data.views || [])).catch(() => {});
  };
  const deleteView = (id) => {
    api.delete(`/geo/saved-views/${id}`).then((r) => setSavedViews(r.data.views || [])).catch(() => {});
  };
  const restoreView = (v) => {
    if (v.kind !== tab) { pendingRestoreRef.current = v; setTab(v.kind); return; }
    selectState(v.code);
    if (v.area) setTimeout(() => drillArea(v.area, v.areaCity), 800);
  };
  // Finish a cross-dataset restore once the new coverage has loaded.
  useEffect(() => {
    const v = pendingRestoreRef.current;
    if (v && v.kind === tab && Object.keys(coverage.states).length) {
      pendingRestoreRef.current = null;
      selectState(v.code);
      if (v.area) setTimeout(() => drillArea(v.area, v.areaCity), 900);
    }
  }, [coverage, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load account-synced saved views.
  useEffect(() => {
    if (!user) return;
    api.get("/geo/saved-views").then((r) => setSavedViews(r.data.views || [])).catch(() => {});
  }, [user]);

  // ---- compare mode ----
  const cmpLabel = (c) => (c.area ? `${tab === "vehicles" ? "" : "ZIP "}${c.area}` : c.name);
  const cmpSub = (c) => (c.area ? `${c.areaCity ? c.areaCity + ", " : ""}${c.code}` : c.code);

  const pinCompare = (code, name) => {
    api.get("/geo/points", { params: { kind: tabRef.current, state: code, limit: 1 } }).then((r) => {
      setCmp((prev) => {
        const entry = { code, name, summary: r.data.summary, total: r.data.total, area: null, areaCity: null };
        if (!prev[0]) return [entry, prev[1]];
        if (!prev[1]) return [prev[0], entry];
        return [entry, prev[1]];
      });
    });
  };
  // Pin a state directly into a chosen slot (A=0 / B=1) — lets you pin the SAME state to
  // both slots and then narrow each to a different ZIP/city for a hyper-local comparison.
  const pinCompareSlot = (i, code, name) => {
    api.get("/geo/points", { params: { kind: tabRef.current, state: code, limit: 1 } }).then((r) => {
      setCmp((prev) => { const n = [...prev]; n[i] = { code, name, summary: r.data.summary, total: r.data.total, area: null, areaCity: null }; return n; });
    });
  };
  pinSlotRef.current = pinCompareSlot;
  const ensureCmpAreas = (code) => {
    if (cmpAreas[code]) return;
    api.get("/geo/zips", { params: { kind: tabRef.current, state: code, limit: 400 } })
      .then((r) => setCmpAreas((m) => ({ ...m, [code]: r.data.areas || [] }))).catch(() => {});
  };
  const setCmpArea = (i, area, areaCity) => {
    const c = cmp[i]; if (!c) return;
    setCmpAreaOpen(null);
    api.get("/geo/points", { params: { kind: tab, state: c.code, area, limit: 1 } }).then((r) => {
      setCmp((prev) => { const n = [...prev]; n[i] = { ...c, area, areaCity, summary: r.data.summary, total: r.data.total }; return n; });
    });
  };
  const clearCmpArea = (i) => {
    const c = cmp[i]; if (!c) return;
    api.get("/geo/points", { params: { kind: tab, state: c.code, limit: 1 } }).then((r) => {
      setCmp((prev) => { const n = [...prev]; n[i] = { ...c, area: null, areaCity: null, summary: r.data.summary, total: r.data.total }; return n; });
    });
  };
  const askContrast = () => {
    if (!cmp[0] || !cmp[1] || !analyst?.ask) return;
    const mk = (c) => `${cmpLabel(c)}${c.area ? ` (${cmpSub(c)})` : ""}: ${(c.summary?.count || 0).toLocaleString()} listings, median ${fmtPrice(c.summary?.median)} (${c.summary?.value_tier})`;
    analyst.ask(`Contrast the luxury ${tab === "vehicles" ? "vehicle" : "real estate"} markets of ${cmpLabel(cmp[0])} and ${cmpLabel(cmp[1])}. ${mk(cmp[0])}. ${mk(cmp[1])}. Compare pricing tiers, inventory depth and what a buyer or investor should know — using only this verified data.`);
  };

  const enterCompare = () => { setCompareMode(true); setPanel(null); setAreaView(null); clearMarkers(); };
  const exitCompare = () => { setCompareMode(false); setCmp([null, null]); };

  // ---- share snapshot (branded PNG) ----
  const drawCompareCard = () => {
    const W = 1240, H = 840;
    const out = document.createElement("canvas"); out.width = W; out.height = H;
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#090a0f"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#f8fafc"; ctx.font = "700 46px Georgia, serif"; ctx.fillText("VAELITH INTELLIGENCE", 56, 78);
    ctx.fillStyle = "#60a5fa"; ctx.font = "600 20px monospace";
    const scopeWord = cmp[0].area && cmp[1].area ? (tab === "vehicles" ? "CITY" : "ZIP") : "STATE";
    ctx.fillText(`${scopeWord} COMPARISON · ${kindLabel.toUpperCase()}`, 56, 108);
    ctx.strokeStyle = "#1e2635"; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(56, 132); ctx.lineTo(W - 56, 132); ctx.stroke();
    ctx.strokeStyle = "#1e2635"; ctx.beginPath(); ctx.moveTo(W / 2, 170); ctx.lineTo(W / 2, H - 90); ctx.stroke();
    ctx.fillStyle = "#334155"; ctx.font = "700 56px Georgia, serif"; ctx.textAlign = "center"; ctx.fillText("VS", W / 2, 470); ctx.textAlign = "left";
    const rows = [["Listings", (c) => (c.summary?.count || 0).toLocaleString()], ["Median Price", (c) => fmtPrice(c.summary?.median)], ["Value Tier", (c) => c.summary?.value_tier || "—"], [tab === "vehicles" ? "Median Miles" : "Median $/Sqft", (c) => "—"], ["Scope", (c) => (c.area ? (tab === "vehicles" ? "City" : "ZIP") : "State")]];
    [{ x: 90, c: cmp[0] }, { x: W / 2 + 74, c: cmp[1] }].forEach(({ x, c }) => {
      ctx.fillStyle = "#f8fafc"; ctx.font = "700 56px Georgia, serif"; ctx.fillText(cmpLabel(c), x, 232);
      ctx.fillStyle = "#94a3b8"; ctx.font = "400 22px monospace"; ctx.fillText(cmpSub(c), x, 266);
      let y = 340;
      rows.forEach(([label, fn]) => {
        ctx.fillStyle = "#64748b"; ctx.font = "600 15px monospace"; ctx.fillText(String(label).toUpperCase(), x, y);
        ctx.fillStyle = "#e2e8f0"; ctx.font = "700 30px Georgia, serif"; ctx.fillText(String(fn(c)), x, y + 38);
        y += 86;
      });
    });
    ctx.fillStyle = "#475569"; ctx.font = "400 16px monospace"; ctx.fillText(`Verified luxury inventory · Generated ${new Date().toLocaleDateString()} · 0 live API calls`, 56, H - 32);
    const link = document.createElement("a");
    const tag0 = cmp[0].area || cmp[0].code, tag1 = cmp[1].area || cmp[1].code;
    link.download = `vaelith-compare-${tag0}-vs-${tag1}-${tab}.png`.toLowerCase();
    link.href = out.toDataURL("image/png"); link.click();
  };

  const shareSnapshot = async () => {
    // Compare mode with both states pinned -> clean two-state comparison card for client emails.
    if (compareMode && cmp[0] && cmp[1]) { setExporting(true); try { drawCompareCard(); } finally { setExporting(false); } return; }
    if (!captureEl.current) return;
    setExporting(true);
    try {
      mapRef.current && mapRef.current.closePopup();
      const src = await html2canvas(captureEl.current, { backgroundColor: "#090a0f", scale: 2, useCORS: true, logging: false });
      const pad = 48, header = 96, footer = 56;
      const out = document.createElement("canvas");
      out.width = src.width + pad * 2; out.height = src.height + header + footer + pad;
      const ctx = out.getContext("2d");
      ctx.fillStyle = "#090a0f"; ctx.fillRect(0, 0, out.width, out.height);
      ctx.fillStyle = "#f8fafc"; ctx.font = "700 40px Georgia, serif";
      ctx.fillText("VAELITH INTELLIGENCE", pad, 60);
      ctx.fillStyle = "#60a5fa"; ctx.font = "600 20px monospace";
      const sub = compareMode ? "Coverage Map · State Comparison" : `Coverage Map · ${panel?.name || "United States"} · ${kindLabel}`;
      ctx.fillText(sub.toUpperCase(), pad, 86);
      ctx.drawImage(src, pad, header);
      ctx.fillStyle = "#475569"; ctx.font = "400 18px monospace";
      ctx.fillText(`Verified luxury inventory · Generated ${new Date().toLocaleDateString()} · 0 live API calls`, pad, out.height - 26);
      const link = document.createElement("a");
      link.download = `vaelith-coverage-${(panel?.code || "us").toLowerCase()}-${tab}.png`;
      link.href = out.toDataURL("image/png"); link.click();
    } finally { setExporting(false); }
  };

  const movers = useMemo(() => {
    const pm = pulseMetric === "median" ? "median" : "count";
    const build = (arr) => {
      const withD = (arr || []).filter((a) => a.deltas && a.deltas[pm] != null && a.deltas[pm] !== 0);
      const sorted = [...withD].sort((a, b) => b.deltas[pm] - a.deltas[pm]);
      return { gainers: sorted.filter((a) => a.deltas[pm] > 0).slice(0, 4), coolers: sorted.filter((a) => a.deltas[pm] < 0).slice(-4).reverse() };
    };
    return { pm, area: build(areaMovers.areas), model: build(modelMovers.models) };
  }, [areaMovers, modelMovers, pulseMetric]);

  const legend = useMemo(() => {
    if (metric === "pulse" && pulseLive) {
      return [
        { c: "rgba(16,185,129,0.9)", label: "gained ▲" },
        { c: "rgba(16,185,129,0.45)", label: "gained slightly" },
        { c: "#334155", label: "no change" },
        { c: "rgba(239,68,68,0.45)", label: "dropped slightly" },
        { c: "rgba(239,68,68,0.9)", label: "dropped ▼" },
        { c: NO_DATA, label: "untracked" },
      ];
    }
    const useVolume = metric === "value" ? false : true;
    const max = metric === "value" ? coverage.max_median : coverage.max_count;
    const fmt = metric === "value" ? fmtPrice : (n) => Math.round(n).toLocaleString();
    const rampL = metric === "value" ? ramp : RAMP_VOLUME;
    return [
      { c: rampL[5], label: `${fmt(max * 0.75)}+` }, { c: rampL[4], label: fmt(max * 0.5) },
      { c: rampL[3], label: fmt(max * 0.25) }, { c: rampL[2], label: fmt(max * 0.08) },
      { c: rampL[1], label: metric === "value" ? "low" : "1+" }, { c: NO_DATA, label: "0" },
    ];
  }, [coverage.max_median, coverage.max_count, metric, pulseLive]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center font-mono-data text-sm text-muted-foreground animate-pulse">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const maxTop = panel?.summary?.top_markets?.[0]?.count || 1;
  const stateDelta = panel && coverage.states[panel.code]?.deltas?.count;
  const stateStreak = panel && coverage.states[panel.code]?.streak;

  return (
    <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-10" data-testid="coverage-map-page">
      {/* Hero header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-border pb-6 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2 font-mono-data text-[11px] uppercase tracking-[0.25em] text-accent">
            <MapIcon className="w-3.5 h-3.5" /> Intelligence Matrix
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-medium">Coverage Map</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-xl">
            Geographical breakdown of verified luxury inventory across the United States. Refreshed with the daily 6&nbsp;AM ET warm — 0 live API calls.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3" data-testid="coverage-stats">
          {[
            { icon: MapPin, label: "Tracked States", val: coverage.state_count },
            { icon: BarChart3, label: `Verified ${kindLabel}`, val: coverage.total.toLocaleString() },
            { icon: TrendingUp, label: "Peak Median", val: fmtPrice(coverage.max_median) },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-border bg-card/60 backdrop-blur px-3.5 py-2.5 min-w-[110px]">
              <div className="flex items-center gap-1.5 font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground mb-1"><s.icon className="w-3 h-3" />{s.label}</div>
              <div className="font-display text-xl leading-tight">{s.val}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Control bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full border border-border bg-card/60 backdrop-blur p-1" data-testid="coverage-tabs">
            {[["homes", HomeIcon, "Homes", "dataset-homes-tab"], ["vehicles", Car, "Vehicles", "dataset-vehicles-tab"]].map(([v, Icon, label, tid]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={tid}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === v ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                <Icon className="w-4 h-4" />{label}</button>
            ))}
          </div>
          <div className="inline-flex rounded-full border border-border bg-card/60 backdrop-blur p-1" data-testid="metric-toggle">
            {[["volume", Activity, "Volume", "metric-volume-toggle"], ["pulse", Zap, "Pulse", "metric-pulse-toggle"]].map(([v, Icon, label, tid]) => (
              <button key={v} onClick={() => setMetric(v)} data-testid={tid}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${metric === v ? (v === "pulse" ? "bg-emerald-500 text-white" : "bg-accent text-accent-foreground") : "text-muted-foreground hover:text-foreground"}`}>
                <Icon className="w-4 h-4" />{label}</button>
            ))}
          </div>
          {metric === "pulse" && (
            <div className="inline-flex rounded-full border border-emerald-500/40 bg-emerald-500/5 backdrop-blur p-1" data-testid="pulse-metric-toggle">
              {[["count", "Inventory", "pulse-metric-count"], ["median", "Median Price", "pulse-metric-median"], ["sub", subLabel, "pulse-metric-sub"]].map(([v, label, tid]) => (
                <button key={v} onClick={() => setPulseMetric(v)} data-testid={tid}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${pulseMetric === v ? "bg-emerald-500 text-white" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
              ))}
            </div>
          )}
          {metric === "pulse" && (
            <div className="inline-flex rounded-full border border-emerald-500/40 bg-emerald-500/5 backdrop-blur p-1" data-testid="pulse-window-toggle">
              {[["warm", "Last warm", "pulse-window-warm"], ["7", "7-day", "pulse-window-7"], ["30", "30-day", "pulse-window-30"]].map(([v, label, tid]) => (
                <button key={v} onClick={() => setPulseWindow(v)} data-testid={tid}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${pulseWindow === v ? "bg-emerald-500 text-white" : "text-muted-foreground hover:text-foreground"}`}>{label}</button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Jump to a state (searchable) */}
          <Popover open={stateOpen} onOpenChange={setStateOpen}>
            <PopoverTrigger asChild>
              <button data-testid="jump-state-trigger"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-border text-muted-foreground hover:text-foreground transition-colors">
                <Search className="w-4 h-4" />{panel?.name && !compareMode ? panel.name : "Jump to state"}<ChevronsUpDown className="w-3.5 h-3.5 opacity-60" /></button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-56" align="end">
              <Command>
                <CommandInput placeholder="Search state…" data-testid="jump-state-input" />
                <CommandList>
                  <CommandEmpty>No tracked state.</CommandEmpty>
                  <CommandGroup>
                    {Object.keys(coverage.states).map((c) => CODE_TO_NAME[c] ? { code: c, name: CODE_TO_NAME[c], count: coverage.states[c].count } : null)
                      .filter(Boolean).sort((a, b) => a.name.localeCompare(b.name)).map((s) => (
                        <CommandItem key={s.code} value={s.name} data-testid={`jump-state-${s.code}`}
                          onSelect={() => { setStateOpen(false); selectState(s.code); }}>
                          <MapPin className="w-3.5 h-3.5 mr-2 opacity-60" />{s.name}
                          <span className="ml-auto font-mono-data text-xs text-muted-foreground">{s.count.toLocaleString()}</span>
                        </CommandItem>
                      ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Jump to an area within the selected state */}
          {!compareMode && panel?.tracked && areaOptions.length > 0 && (
            <Popover open={areaOpen} onOpenChange={setAreaOpen}>
              <PopoverTrigger asChild>
                <button data-testid="jump-area-trigger"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-border text-muted-foreground hover:text-foreground transition-colors">
                  <Search className="w-4 h-4" />{areaWord === "ZIP" ? "ZIP" : "City"}<ChevronsUpDown className="w-3.5 h-3.5 opacity-60" /></button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-60" align="end">
                <Command>
                  <CommandInput placeholder={`Search ${areaWord.toLowerCase()}…`} data-testid="jump-area-input" />
                  <CommandList>
                    <CommandEmpty>No {areaWord.toLowerCase()} found.</CommandEmpty>
                    <CommandGroup>
                      {areaOptions.map((a) => (
                        <CommandItem key={a.area} value={`${a.area} ${a.city || ""}`}
                          onSelect={() => { setAreaOpen(false); drillArea(a.area, a.city); }}>
                          <span className="truncate">{areaWord === "ZIP" ? a.area : a.city}{areaWord === "ZIP" && a.city ? ` · ${a.city}` : ""}</span>
                          <span className="ml-auto font-mono-data text-xs text-muted-foreground">{a.count}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}

          {/* Saved views */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="saved-views-trigger"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-border text-muted-foreground hover:text-foreground transition-colors">
                <Bookmark className="w-4 h-4" />Saved{savedViews.length ? ` (${savedViews.length})` : ""}</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Saved views</DropdownMenuLabel>
              {panel?.tracked && !compareMode && (
                <DropdownMenuItem onSelect={(e) => { e.preventDefault(); saveCurrentView(); }} data-testid="save-current-view">
                  <BookmarkPlus className="w-4 h-4 mr-2 text-accent" />Save current{areaView ? ` (${panel.code} · ${areaView.area})` : ` (${panel.code})`}
                </DropdownMenuItem>
              )}
              {savedViews.length > 0 && <DropdownMenuSeparator />}
              {savedViews.length === 0 ? (
                <div className="px-2 py-3 text-xs text-muted-foreground">No saved views yet. Explore a state and save it.</div>
              ) : savedViews.map((v) => (
                <DropdownMenuItem key={v.id} onSelect={(e) => { e.preventDefault(); restoreView(v); }} data-testid={`saved-view-${v.id}`} className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${v.kind === "vehicles" ? "bg-violet-400" : "bg-accent"}`} />
                  <span className="truncate flex-1">{v.name}{v.area ? ` · ${v.area}` : ""}</span>
                  <span className="font-mono-data text-[9px] uppercase text-muted-foreground">{v.kind === "vehicles" ? "veh" : "home"}</span>
                  <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-red-400" onClick={(e) => { e.stopPropagation(); deleteView(v.id); }} />
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button onClick={compareMode ? exitCompare : enterCompare} data-testid="compare-toggle"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${compareMode ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:text-foreground"}`}>
            <GitCompareArrows className="w-4 h-4" />{compareMode ? "Exit" : "Compare"}</button>
          <button onClick={shareSnapshot} data-testid="share-snapshot-btn" disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border border-border text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Share</button>
        </div>
      </div>

      {/* Helper caption */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4 font-mono-data">
        <Info className="w-3.5 h-3.5 text-accent" />
        {compareMode
          ? "Compare mode — click a state and choose Pin to A or Pin to B (pin the same state twice to compare two ZIPs), then narrow each slot and Share exports a comparison card."
          : metric === "pulse"
          ? (pulseLive
              ? `Pulse view — states tint green/red by the change in ${pulseMetric === "median" ? "median price" : pulseMetric === "sub" ? subLabel.toLowerCase() : "inventory"} since the last daily warm. Toggle the metric above.`
              : `Pulse is calibrating — no verified ${pulseMetric === "median" ? "median-price" : pulseMetric === "sub" ? subLabel.toLowerCase() : "inventory"} change since the last 6 AM warm. States are shaded by inventory density for now; they’ll tint green/red as the market moves between daily warms.`)
          : `Click a state (or use Jump to state) to see its ${areaWord} bubbles. Each bubble’s number is how many luxury ${kindLabel.toLowerCase()} we track in that ${areaWord}. Click a bubble to view the listings.`}
      </div>

      {/* Map + panel */}
      <div ref={captureEl} className="flex flex-col lg:flex-row gap-5">
        <div className="relative rounded-2xl overflow-hidden border border-border flex-1" style={{ height: "70vh", minHeight: 500, boxShadow: "0 24px 60px rgba(0,0,0,0.6)" }}>
          <div ref={mapEl} className="absolute inset-0" data-testid="leaflet-choropleth-map" />
          <div className="absolute z-[500] bottom-4 left-4 bg-background/85 backdrop-blur-md border border-border rounded-xl px-3 py-2.5" data-testid="coverage-legend">
            <div className="flex items-center gap-1.5 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5"><Layers className="w-3 h-3" />{metric === "pulse" ? (pulseLive ? "Since last warm" : `${kindLabel} density`) : `${kindLabel} ${metric === "value" ? "median price" : "density"}`}</div>
            <div className="flex flex-col gap-1">
              {legend.map((l, i) => (<div key={i} className="flex items-center gap-2 text-[11px] text-muted-foreground"><span className="w-3.5 h-3.5 rounded-sm border border-border" style={{ background: l.c }} />{l.label}</div>))}
            </div>
          </div>
          {loadingPts && (<div className="absolute z-[500] top-4 right-4 bg-background/90 backdrop-blur-md border border-border rounded-xl px-3.5 py-2.5 font-mono-data text-xs flex items-center gap-2 text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading…</div>)}
          {/* Local Movers — biggest gaining/cooling ZIPs (or vehicle models) since the last warm (Pulse mode) */}
          {(() => {
            if (!(metric === "pulse" && panel?.tracked && !areaView)) return null;
            const modelsOn = tab === "vehicles" && moversMode === "models";
            const set = modelsOn ? movers.model : movers.area;
            const setAvailable = modelsOn ? modelMovers.available : areaMovers.available;
            if (!setAvailable || (set.gainers.length === 0 && set.coolers.length === 0)) {
              if (!(tab === "vehicles")) return null; // homes: nothing to show
            }
            const rowLabel = (a) => (modelsOn ? a.label : (tab === "vehicles" ? a.city : `${a.area}${a.city ? ` · ${a.city}` : ""}`));
            const rowKey = (a) => (modelsOn ? a.label : a.area);
            const mag = (a) => (movers.pm === "median" ? fmtPrice(Math.abs(a.deltas.median)) : Math.abs(a.deltas.count).toLocaleString());
            const onRow = (a) => {
              if (modelsOn) navigate(`/vehicles?make=${encodeURIComponent(a.make || "")}&model=${encodeURIComponent(a.model || "")}`);
              else drillArea(a.area, a.city);
            };
            const Row = ({ a, up, kind }) => (
              <button key={`${kind}-${rowKey(a)}`} onClick={() => onRow(a)} data-testid={`mover-${kind}-${rowKey(a)}`}
                title={modelsOn ? "View listings" : "Show on map"}
                className="w-full flex items-center justify-between gap-2 py-0.5 text-left transition-opacity hover:opacity-80">
                <span className="truncate text-[11px] flex items-center gap-1">
                  {a.streak && a.streak.len >= 3 && <Flame className="w-3 h-3 text-amber-400 shrink-0" data-testid={`streak-${kind}-${rowKey(a)}`} />}
                  {rowLabel(a)}
                </span>
                <span className={`font-mono-data text-[10px] shrink-0 ${up ? "text-emerald-400" : "text-red-400"}`}>{up ? "▲" : "▼"}{mag(a)}</span>
              </button>
            );
            return (
              <div className="absolute z-[500] bottom-4 right-4 w-52 bg-background/90 backdrop-blur-md border border-border rounded-xl px-3 py-2.5" data-testid="area-movers-card">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground"><Zap className="w-3 h-3 text-emerald-400" />Local Movers</div>
                  {tab === "vehicles" && (
                    <div className="inline-flex rounded-full border border-border p-0.5" data-testid="movers-mode-toggle">
                      {[["cities", "Cities"], ["models", "Models"]].map(([v, l]) => (
                        <button key={v} onClick={() => setMoversMode(v)} data-testid={`movers-mode-${v}`}
                          className={`px-2 py-0.5 rounded-full text-[9px] font-medium transition-colors ${moversMode === v ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="font-mono-data text-[8px] uppercase tracking-wider text-muted-foreground mb-2">{modelsOn ? "Models" : (tab === "vehicles" ? "Cities" : "ZIPs")} · {pulseMetric === "median" ? "Median" : "Inventory"}</div>
                {set.gainers.length === 0 && set.coolers.length === 0 ? (
                  <div className="text-[10px] text-muted-foreground py-1" data-testid="movers-empty">No movement yet — builds after the next warm.</div>
                ) : (
                  <>
                    {set.gainers.length > 0 && (
                      <div className="mb-2">
                        <div className="font-mono-data text-[9px] uppercase tracking-wider text-emerald-400 mb-1">Gaining</div>
                        {set.gainers.map((a) => <Row key={`g-${rowKey(a)}`} a={a} up kind="gain" />)}
                      </div>
                    )}
                    {set.coolers.length > 0 && (
                      <div>
                        <div className="font-mono-data text-[9px] uppercase tracking-wider text-red-400 mb-1">Cooling</div>
                        {set.coolers.map((a) => <Row key={`c-${rowKey(a)}`} a={a} up={false} kind="cool" />)}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}
        </div>

        {/* Side panel */}
        <div className="w-full lg:w-[360px] shrink-0 rounded-2xl border border-border bg-card/70 backdrop-blur-xl p-5 flex flex-col" data-testid="selected-state-panel">
          <AnimatePresence mode="wait">
            {compareMode ? (
              <motion.div key="cmp" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col" data-testid="compare-panel">
                <div className="flex items-center gap-2 mb-4"><GitCompareArrows className="w-4 h-4 text-accent" /><h2 className="font-display text-xl">Compare {cmp[0]?.area && cmp[1]?.area ? (areaWord === "ZIP" ? "ZIPs" : "Cities") : "Markets"}</h2></div>
                <div className="grid grid-cols-2 gap-3 flex-1 content-start">
                  {[0, 1].map((i) => {
                    const c = cmp[i];
                    return (
                      <div key={i} className="rounded-xl border border-border bg-background/40 p-3 min-h-[150px]" data-testid={`compare-slot-${i}`}>
                        {c ? (
                          <>
                            <div className="flex items-center justify-between mb-2">
                              <div className="font-display text-lg leading-none">{cmpLabel(c)}</div>
                              <button onClick={() => setCmp((p) => { const n = [...p]; n[i] = null; return n; })} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
                            </div>
                            <div className="text-xs text-muted-foreground mb-2 truncate">{cmpSub(c)}</div>
                            {/* narrow to a ZIP/city */}
                            <Popover open={cmpAreaOpen === i} onOpenChange={(o) => { setCmpAreaOpen(o ? i : null); if (o) ensureCmpAreas(c.code); }}>
                              <PopoverTrigger asChild>
                                <button data-testid={`compare-area-trigger-${i}`}
                                  className="w-full mb-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border border-border text-muted-foreground hover:text-foreground transition-colors">
                                  <Search className="w-3 h-3" />{c.area ? `${areaWord === "ZIP" ? "ZIP " : ""}${c.area}` : `Narrow to ${areaWord}`}<ChevronsUpDown className="w-3 h-3 opacity-60 ml-auto" /></button>
                              </PopoverTrigger>
                              <PopoverContent className="p-0 w-56" align="start">
                                <Command>
                                  <CommandInput placeholder={`Search ${areaWord.toLowerCase()}…`} />
                                  <CommandList>
                                    <CommandEmpty>No {areaWord.toLowerCase()} found.</CommandEmpty>
                                    <CommandGroup>
                                      {c.area && <CommandItem value="__whole_state" onSelect={() => clearCmpArea(i)}><MapPin className="w-3.5 h-3.5 mr-2 opacity-60" />Whole state ({c.code})</CommandItem>}
                                      {(cmpAreas[c.code] || []).map((a) => (
                                        <CommandItem key={a.area} value={`${a.area} ${a.city || ""}`} data-testid={`compare-area-${i}-${a.area}`} onSelect={() => setCmpArea(i, a.area, a.city)}>
                                          <span className="truncate">{areaWord === "ZIP" ? a.area : a.city}{areaWord === "ZIP" && a.city ? ` · ${a.city}` : ""}</span>
                                          <span className="ml-auto font-mono-data text-xs text-muted-foreground">{a.count}</span>
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            {[["Listings", (c.summary?.count || 0).toLocaleString()], ["Median", fmtPrice(c.summary?.median)], ["Tier", c.summary?.value_tier || "—"], ["Scope", c.area ? (areaWord === "ZIP" ? "ZIP" : "City") : "State"]].map(([k, v]) => (
                              <div key={k} className="flex items-center justify-between py-1 border-b border-border/50 last:border-0">
                                <span className="font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground">{k}</span>
                                <span className="text-sm truncate ml-2">{v}</span>
                              </div>
                            ))}
                          </>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                            <span className="font-display text-2xl opacity-30 mb-1">{i === 0 ? "A" : "B"}</span>
                            <span className="text-xs">Click a state<br />to pin</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button onClick={askContrast} disabled={!cmp[0] || !cmp[1]} data-testid="ask-ai-contrast-btn"
                  className="mt-4 w-full flex items-center justify-center gap-2 rounded-xl py-3 px-4 text-white font-medium text-sm transition-transform hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0"
                  style={{ background: "linear-gradient(90deg,#2563eb,#6d28d9)", boxShadow: "0 10px 30px rgba(37,99,235,0.35)" }}>
                  <Sparkles className="w-4 h-4" /> Ask Vaelith AI to contrast</button>
              </motion.div>
            ) : !panel ? (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col">
                {digest.available && (digest.gainers?.length > 0 || digest.price_movers?.length > 0) ? (
                  <div className="w-full mb-5 rounded-xl border border-border bg-background/40 p-4" data-testid="movers-digest">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1.5 font-mono-data text-[10px] uppercase tracking-wider text-accent"><Zap className="w-3.5 h-3.5" />New This Week</div>
                      <span className="font-mono-data text-[9px] text-muted-foreground">over {digest.days_span}d · {kindLabel.toLowerCase()}</span>
                    </div>
                    {digest.gainers?.length > 0 && (
                      <div className="mb-3">
                        <div className="font-mono-data text-[9px] uppercase tracking-wider text-emerald-400 mb-1.5">Inventory Climbing</div>
                        {digest.gainers.slice(0, 4).map((r) => (
                          <button key={r.code} onClick={() => selectState(r.code)} data-testid={`digest-gain-${r.code}`}
                            className="w-full flex items-center justify-between py-0.5 hover:opacity-80 transition-opacity">
                            <span className="text-xs truncate flex items-center gap-1">
                              {coverage.states[r.code]?.streak?.len >= 3 && <Flame className="w-3 h-3 text-amber-400 shrink-0" />}
                              {CODE_TO_NAME[r.code] || r.code}
                            </span>
                            <span className="font-mono-data text-[10px] text-emerald-400 shrink-0">▲{r.delta_count.toLocaleString()}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {digest.price_movers?.length > 0 && (
                      <div>
                        <div className="font-mono-data text-[9px] uppercase tracking-wider text-violet-400 mb-1.5">Biggest Price Moves</div>
                        {digest.price_movers.slice(0, 4).map((r) => (
                          <button key={r.code} onClick={() => selectState(r.code)} data-testid={`digest-price-${r.code}`}
                            className="w-full flex items-center justify-between py-0.5 hover:opacity-80 transition-opacity">
                            <span className="text-xs truncate">{CODE_TO_NAME[r.code] || r.code}</span>
                            <span className="font-mono-data text-[10px] shrink-0" style={{ color: r.delta_median > 0 ? "#34d399" : "#f87171" }}>{r.delta_median > 0 ? "▲" : "▼"}{fmtPrice(Math.abs(r.delta_median))}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 pt-2.5 border-t border-border/60 font-mono-data text-[9px] text-muted-foreground text-center">Tap a state to explore · momentum builds daily</div>
                  </div>
                ) : null}
                <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
                  <div className="w-12 h-12 rounded-full border border-border flex items-center justify-center mb-4"><MapPin className="w-5 h-5 text-muted-foreground" /></div>
                  <div className="font-display text-lg">Select a state</div>
                  <p className="text-sm text-muted-foreground mt-1.5 max-w-[220px]">Click any tracked state to see its {areaWord} bubbles and open its market intelligence.</p>
                </div>
              </motion.div>
            ) : (
              <motion.div key={panel.code + metric} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.3 }} className="flex-1 flex flex-col" data-testid="state-info-summary">
                <div className="flex items-start justify-between gap-2 mb-4">
                  <div>
                    <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{panel.code}</div>
                    <h2 className="font-display text-2xl tracking-tight leading-none">{panel.name}</h2>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className={`font-mono-data text-[9px] uppercase tracking-wider border rounded-full px-2 py-1 ${panel.tracked ? "text-emerald-400 border-emerald-500/40" : "text-muted-foreground border-border"}`}>{panel.tracked ? "Tracked" : "Untracked"}</span>
                    {panel.tracked && (stateDelta ? (
                      <span className="font-mono-data text-[9px] flex items-center gap-0.5 text-emerald-400" data-testid="pulse-chip"><ArrowUp className="w-2.5 h-2.5" />+{stateDelta} since last warm</span>
                    ) : coverage.pulse_available ? (
                      <span className="font-mono-data text-[9px] text-muted-foreground" data-testid="pulse-chip">no change since last warm</span>
                    ) : (
                      <span className="font-mono-data text-[9px] text-muted-foreground" data-testid="pulse-chip">pulse baseline set</span>
                    ))}
                    {stateStreak && stateStreak.len >= 3 && (
                      <span className="font-mono-data text-[9px] flex items-center gap-0.5 text-amber-400 border border-amber-500/40 rounded-full px-2 py-0.5" data-testid="state-streak-chip">
                        <Flame className="w-2.5 h-2.5" />{stateStreak.len}-warm {stateStreak.dir > 0 ? "up" : "down"} streak
                      </span>
                    )}
                  </div>
                </div>

                {!panel.tracked ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center py-10">
                    <Gauge className="w-8 h-8 text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground max-w-[220px]">We don't yet track luxury {kindLabel.toLowerCase()} inventory in {panel.name}. It's on the expansion roadmap.</p>
                  </div>
                ) : (
                  <>
                    {areaView && (
                      <div className="mb-4 rounded-xl border border-accent/40 bg-accent/10 p-3" data-testid="area-drill-banner">
                        <button onClick={backToAreas} className="flex items-center gap-1.5 text-xs text-accent mb-1.5 hover:underline" data-testid="back-to-areas-btn"><ArrowLeft className="w-3.5 h-3.5" />Back to all {areaWord}s</button>
                        <div className="text-sm font-medium">{tab === "vehicles" ? "" : "ZIP "}{areaView.area}{areaView.city && tab !== "vehicles" ? ` · ${areaView.city}` : ""}</div>
                        <div className="font-mono-data text-[10px] text-muted-foreground">{(areaView.count || 0).toLocaleString()} {kindLabel.toLowerCase()} in this {areaWord}</div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2.5 mb-5">
                      {[
                        { label: "Listings", val: (panel.summary?.count || 0).toLocaleString(), icon: BarChart3 },
                        { label: "Median Price", val: fmtPrice(panel.summary?.median), icon: DollarSign },
                        { label: "Value Tier", val: panel.summary?.value_tier || "—", icon: TrendingUp },
                        { label: `Top ${areaWord}s`, val: panel.summary?.top_markets?.length || 0, icon: MapPin },
                      ].map((m) => (
                        <div key={m.label} className="rounded-xl border border-border bg-background/40 px-3 py-2.5">
                          <div className="flex items-center gap-1.5 font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground mb-1"><m.icon className="w-3 h-3" />{m.label}</div>
                          <div className="font-display text-lg leading-tight truncate pb-0.5">{m.val}</div>
                        </div>
                      ))}
                    </div>
                    {/* Pulse Timeline — 7-day trend sparkline (builds as daily snapshots accrue) */}
                    <div className="mb-5 rounded-xl border border-border bg-background/40 px-3.5 py-3" data-testid="pulse-timeline">
                      {(() => {
                        const series = panel.timeline || [];
                        const field = metric === "value" ? "median" : "count";
                        const label = metric === "value" ? "Median Price" : "Inventory";
                        const pts = series.map((s) => s[field]);
                        const dates = series.map((s) => s.date);
                        const fmtVal = (v) => (field === "median" ? fmtPrice(v) : Number(v || 0).toLocaleString());
                        if (pts.length < 2) {
                          return (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5 font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground"><Activity className="w-3 h-3" />Pulse Timeline · {label}</div>
                              <div className="font-mono-data text-[9px] text-muted-foreground" data-testid="pulse-timeline-building">Day {Math.max(1, pts.length)} of 30 — building…</div>
                            </div>
                          );
                        }
                        const first = pts.find((v) => v != null && v !== 0) ?? pts[0];
                        const last = pts[pts.length - 1];
                        const pctc = first ? ((last - first) / first) * 100 : 0;
                        const up = pctc > 0.05, down = pctc < -0.05;
                        const color = up ? "#34d399" : down ? "#f87171" : "#60a5fa";
                        const TrendIcon = up ? TrendingUp : down ? TrendingDown : Minus;
                        return (
                          <>
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-1.5 font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground"><Activity className="w-3 h-3" />Pulse Timeline · {label}</div>
                              <div className="flex items-center gap-1 font-mono-data text-[11px]" style={{ color }} data-testid="pulse-timeline-trend">
                                <TrendIcon className="w-3 h-3" />{pctc > 0 ? "+" : ""}{pctc.toFixed(1)}%
                              </div>
                            </div>
                            <div className="flex items-end justify-between gap-3">
                              <Sparkline points={pts} dates={dates} color={color} format={fmtVal} />
                              <div className="text-right">
                                <div className="font-display text-base leading-none">{field === "median" ? fmtPrice(last) : last.toLocaleString()}</div>
                                <div className="font-mono-data text-[9px] text-muted-foreground mt-0.5">{pts.length}-day trend</div>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    {panel.summary?.top_markets?.length > 0 && (
                      <div className="mb-5">
                        <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mb-2.5">Top Markets</div>
                        <div className="space-y-2">
                          {panel.summary.top_markets.map((mk) => (
                            <div key={mk.city} className="flex items-center gap-3">
                              <div className="text-sm w-24 truncate">{mk.city}</div>
                              <div className="flex-1 h-1.5 rounded-full bg-background/60 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(mk.count / maxTop) * 100}%`, background: `rgb(${accentRGB})` }} /></div>
                              <div className="font-mono-data text-xs text-muted-foreground w-8 text-right">{mk.count}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-auto pt-2">
                      <button onClick={() => { const s = panel.summary || {}; analyst?.ask?.(`Give me a market intelligence summary of luxury ${tab === "vehicles" ? "vehicles" : "real estate"} in ${panel.name}. We track ${(s.count || 0).toLocaleString()} verified listings with a median price of ${fmtPrice(s.median)} (value tier: ${s.value_tier}). Top markets: ${(s.top_markets || []).slice(0, 4).map((m) => `${m.city} (${m.count})`).join(", ") || "n/a"}. Explain pricing, notable segments, and what stands out — using only this verified data.`); }}
                        data-testid="ask-vaelith-ai-market-button"
                        className="w-full flex items-center justify-center gap-2 rounded-xl py-3 px-4 text-white font-medium text-sm transition-transform hover:-translate-y-0.5"
                        style={{ background: "linear-gradient(90deg,#2563eb,#6d28d9)", boxShadow: "0 10px 30px rgba(37,99,235,0.35)" }}>
                        <Sparkles className="w-4 h-4" /> Ask Vaelith AI to summarize this market</button>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
